import React, { useState } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  User, Bell, Shield, Palette, Globe, CreditCard,
  Users, Link2, HelpCircle, Moon, Sun, Check,
  ChevronRight, Camera, Mail, Phone, MapPin, Building2
} from 'lucide-react';

const tabs = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'integrations', label: 'Integrations', icon: Link2 },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

const gradientOptions = [
  { id: 'ocean', name: 'Ocean', colors: ['#1E3A5F', '#2D8CFF'] },
  { id: 'golden', name: 'Golden', colors: ['#D4AF37', '#FCD34D'] },
  { id: 'aurora', name: 'Aurora', colors: ['#667eea', '#f64f59'] },
  { id: 'emerald', name: 'Emerald', colors: ['#10B981', '#047857'] },
  { id: 'sunset', name: 'Sunset', colors: ['#F59E0B', '#EF4444'] },
];

const Settings = () => {
  const { isDark, toggleTheme, customization, updateCustomization } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedGradient, setSelectedGradient] = useState('ocean');

  return (
    <MainLayout title="Settings" subtitle="Manage your preferences">
      <div className="grid grid-cols-4 gap-6">
        {/* Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="glass-card p-4"
        >
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white'
                    : 'hover:bg-gray-500/10'
                }`}
                whileHover={{ x: activeTab === tab.id ? 0 : 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <tab.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{tab.label}</span>
              </motion.button>
            ))}
          </nav>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="col-span-3 glass-card p-6"
        >
          {activeTab === 'profile' && (
            <div>
              <h2 className="text-xl font-bold mb-6">Profile Settings</h2>
              
              {/* Avatar */}
              <div className="flex items-center gap-6 mb-8">
                <div className="relative">
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] flex items-center justify-center text-white text-3xl font-bold">
                    VS
                  </div>
                  <motion.button
                    className="absolute -bottom-2 -right-2 p-2 rounded-full bg-blue-500 text-white"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Camera className="w-4 h-4" />
                  </motion.button>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Adv. VSKR</h3>
                  <p className="text-sm text-gray-500">Premium Plan • Member since Jan 2024</p>
                </div>
              </div>

              {/* Form */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <input type="text" defaultValue="Adv. VSKR" className="w-full input-glass" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Bar Council No.</label>
                  <input type="text" defaultValue="D/1234/2020" className="w-full input-glass" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input type="email" defaultValue="vskr@legal.com" className="w-full input-glass" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input type="tel" defaultValue="+91 98765 43210" className="w-full input-glass" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Office Address</label>
                  <textarea rows={3} defaultValue="123, Legal Chambers, Connaught Place, New Delhi - 110001" className="w-full input-glass" />
                </div>
              </div>

              <motion.button
                className="mt-6 px-6 py-3 rounded-xl btn-primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Save Changes
              </motion.button>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div>
              <h2 className="text-xl font-bold mb-6">Appearance Settings</h2>
              
              {/* Theme Toggle */}
              <div className="mb-8">
                <h3 className="font-semibold mb-4">Theme Mode</h3>
                <div className="flex items-center gap-4">
                  <motion.button
                    onClick={() => isDark && toggleTheme()}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      !isDark ? 'border-blue-500 bg-blue-500/10' : 'border-gray-500/20'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Sun className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                    <p className="font-medium">Light Mode</p>
                  </motion.button>
                  <motion.button
                    onClick={() => !isDark && toggleTheme()}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      isDark ? 'border-blue-500 bg-blue-500/10' : 'border-gray-500/20'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Moon className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                    <p className="font-medium">Dark Mode</p>
                  </motion.button>
                </div>
              </div>

              {/* Accent Color */}
              <div className="mb-8">
                <h3 className="font-semibold mb-4">Accent Gradient</h3>
                <div className="grid grid-cols-5 gap-4">
                  {gradientOptions.map((gradient) => (
                    <motion.button
                      key={gradient.id}
                      onClick={() => setSelectedGradient(gradient.id)}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        selectedGradient === gradient.id ? 'border-blue-500' : 'border-transparent'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div
                        className="w-full h-12 rounded-lg mb-2"
                        style={{ background: `linear-gradient(135deg, ${gradient.colors[0]}, ${gradient.colors[1]})` }}
                      />
                      <p className="text-sm font-medium">{gradient.name}</p>
                      {selectedGradient === gradient.id && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center"
                        >
                          <Check className="w-4 h-4 text-white" />
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Effects */}
              <div>
                <h3 className="font-semibold mb-4">Visual Effects</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Glassmorphism', key: 'glassmorphism', enabled: true },
                    { label: 'Hover Animations', key: 'hoverEffects', enabled: true },
                    { label: '3D Depth Effects', key: 'depth3d', enabled: true },
                    { label: 'Glow Effects', key: 'glow', enabled: true },
                  ].map((effect) => (
                    <div key={effect.key} className="flex items-center justify-between p-4 rounded-xl bg-gray-500/5">
                      <span className="font-medium">{effect.label}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked={effect.enabled} className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-500/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div>
              <h2 className="text-xl font-bold mb-6">Notification Settings</h2>
              <div className="space-y-4">
                {[
                  { label: 'Email Notifications', desc: 'Receive updates via email', enabled: true },
                  { label: 'Push Notifications', desc: 'Browser push notifications', enabled: true },
                  { label: 'SMS Alerts', desc: 'Critical alerts via SMS', enabled: false },
                  { label: 'WhatsApp Updates', desc: 'Receive updates on WhatsApp', enabled: true },
                  { label: 'Case Updates', desc: 'New orders and next dates', enabled: true },
                  { label: 'Payment Reminders', desc: 'Invoice and payment alerts', enabled: true },
                ].map((notif) => (
                  <div key={notif.label} className="flex items-center justify-between p-4 rounded-xl bg-gray-500/5">
                    <div>
                      <p className="font-medium">{notif.label}</p>
                      <p className="text-sm text-gray-500">{notif.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked={notif.enabled} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-500/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {['security', 'team', 'integrations', 'billing'].includes(activeTab) && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                {activeTab === 'security' && <Shield className="w-8 h-8 text-blue-500" />}
                {activeTab === 'team' && <Users className="w-8 h-8 text-blue-500" />}
                {activeTab === 'integrations' && <Link2 className="w-8 h-8 text-blue-500" />}
                {activeTab === 'billing' && <CreditCard className="w-8 h-8 text-blue-500" />}
              </div>
              <h3 className="text-xl font-semibold mb-2 capitalize">{activeTab} Settings</h3>
              <p className="text-gray-500">This section is coming soon</p>
            </div>
          )}
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default Settings;
